import classNames from "classnames";
import classes from "./AvailabilityTile.module.less";
import {Stacker} from "~/common/components/Stacker/Stacker";
import {Card} from "~/Configurators/Linear/components/Card";
import {Divider} from "~/common/components/Divider";
import {Trans, useTranslation} from "react-i18next";
import dayjs from "dayjs";

import {Bar} from "react-chartjs-2";

import {Chart as ChartJS} from "chart.js/auto";

import {
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import {
  C_CHART_4,
  C_ON_SURFACE,
  C_PRIMARY_CONTAINER,
  C_SECONDARY_DISABLED,
  C_SURFACE,
} from "~/common/components/icon/Colors";
import type {InStockEntryData, PackagingInformationEntry} from "~/models/ProductData";
import {groupByFn, isNonNull} from "~/utils/reducers";
import CenterContents from "~/common/components/CenterContents";
import {CircularProgress} from "~/common/components/CircularProgress";
import {centeredTextPlugin, formatYTicks, rltPlugin, useRerenderOnResize} from "~/FreeCapacityPush/graphUtils";
import {InfoCircle} from "~/FreeCapacityPush/components/InfoCircle/InfoCircle";
import {NumberFormatter as NF} from "~/utils/NumberFormatter";
import {FontSize, getFontStyle} from "~/common/components/materialInput/Fonts";
import {useNumericScreenSize} from "~/common/hooks/useNumericScreenSize";
import {Alert} from "~/common/m3/Alert/Alert";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Props {
  className?: string;
  packageCode?: string;
  product: ProductDescriptor | null;
}

const MAX_MONTHS = 9;
const MIN_MONTHS = 3;

export const AvailabilityTile = ({ className, product, packageCode }: Props) => {
  const { t } = useTranslation();

  const screenSize = useNumericScreenSize();

  let isLoading = false;
  if (product?.isAvailabilityLoading) {
    isLoading = true;
  }

  let missingRlt = false;
  let showYTicks = false;
  let rltMonthAmount = 0;

  const data: ChartData<"bar"> = {
    labels: undefined,
    datasets: [
      {
        label: t("page.fcc.availability.chart.inStock"),
        data: [],
        borderRadius: 4,
        backgroundColor: C_PRIMARY_CONTAINER,
        barThickness: 18,
      },
      {
        label: t("page.fcc.availability.chart.plannedAvailability"),
        data: [],
        borderRadius: 4,
        backgroundColor: C_CHART_4,
        barThickness: 18,
      },
    ],
  };

  if (product && packageCode && !product.isAvailabilityError) {
    const packInfo = product.availability?.packagingInformation.find(
      (pack) => pack.packCode === packageCode,
    ) as PackagingInformationEntry;

    if (packInfo) {
      const fifteenDigit = product.base.fifteenDigit[packageCode!];

      if (fifteenDigit?.fccAvailabilityMonths === undefined || fifteenDigit?.fccAvailabilityMonths === null) {
        missingRlt = true;
      } else {
        const monthAmount = 1 + fifteenDigit.fccAvailabilityMonths;

        rltMonthAmount =
          1 +
          dayjs()
            .add(fifteenDigit.rlt ?? 0, "days")
            .get("month") -
          dayjs().get("month");
        if (rltMonthAmount <= 0) {
          rltMonthAmount += 12;
        }

        const monthNames = Array.from({ length: monthAmount }, (_, i) => dayjs().add(i, "months").format("MMM"));

        data.labels = monthNames;
        showYTicks = true;
        data.datasets[0].data = groupByMonth(packInfo.totalWarehouse.inStock, monthAmount)
          .map((entry) => entry.quantity)
          .slice(0, monthAmount);
        data.datasets[1].data = groupByMonth(packInfo.totalWarehouse.inProduction, monthAmount)
          .map((entry) => entry.quantity)
          .slice(0, monthAmount);
      }
    }
  }

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 24,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom" as const,
        labels: {
          boxWidth: 16,
          boxHeight: 12,
          borderRadius: 4,
          useBorderRadius: true,
        },
      },
      tooltip: {
        displayColors: true,
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            if (context.parsed.y !== null) {
              return NF.getDefaultFormatter().format(context.parsed.y);
            }
            return "";
          },
        },
        // @ts-ignore - this exists
        labelTextColor: C_ON_SURFACE,
        titleColor: C_ON_SURFACE,
        bodyColor: C_ON_SURFACE,
        backgroundColor: C_SURFACE,
      },
    },
    scales: {
      y: {
        stacked: true,
        ticks: {
          display: showYTicks,
          callback: formatYTicks,
        },
        border: {
          display: showYTicks,
        },
      },
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
    },
  };

  const popupContent = (
    <div className={classes.tooltipContent}>
      <Trans i18nKey={"page.fcc.availability.tooltip"} />
    </div>
  );

  const designation = product?.base.designation ?? "Material";

  const height = 186 + 24;
  const [containerRef, chartRef] = useRerenderOnResize();

  const centeredTextFont = screenSize >= useNumericScreenSize.desktopL ? FontSize.TitleLarge : FontSize.TitleMedium;
  const centeredTextFontSize = getFontStyle(centeredTextFont).fontSize;

  return (
    <Card className={classNames(classes.root, className)}>
      <InfoCircle content={popupContent} placement={"left"} className={classes.infoIcon} />
      <div className={classes.title}>
        <span>{t("page.fcc.availability.title")}</span>
      </div>
      <Stacker className={classes.subtitle} direction={"row"} gap={8}>
        <span>{designation}</span>
        <Divider orientation={"vertical"} flexItem />
        <span>{t("page.fcc.freeCapacity.pcs")}</span>
      </Stacker>
      <Stacker className={classes.content} gap={24}>
        {missingRlt && <Alert severity={"info"}>{t("page.fcc.availability.noRlt")}</Alert>}
        {isLoading && (
          <CenterContents>
            <CircularProgress />
          </CenterContents>
        )}
        {!isLoading && (
          <div className={"w-100"} style={{ height: height + "px" }} ref={containerRef}>
            <Bar
              key={(product?.base.code ?? "") + packageCode}
              // @ts-ignore
              ref={chartRef}
              height={height}
              options={options}
              data={data}
              plugins={[
                !product && centeredTextPlugin(t("page.fcc.freeCapacity.noTrain"), centeredTextFontSize, C_ON_SURFACE),
                // noinspection PointlessBooleanExpressionJS - not pointless. TS goes crazy otherwise
                !!missingRlt &&
                  centeredTextPlugin(t("page.fcc.availability.noRlt"), centeredTextFontSize, C_SECONDARY_DISABLED),
                rltPlugin(rltMonthAmount, t("page.fcc.availability.rltLabel")),
              ].filter(isNonNull)}
            />
          </div>
        )}
      </Stacker>
    </Card>
  );
};

const DATE_FORMAT = "YYYY-MM";
function groupByMonth(entries: InStockEntryData[], maxMonths: number) {
  const byMonth = groupByFn(
    entries,
    (entry) => dayjs(entry.date).format(DATE_FORMAT),
    (entry) => entry.quantity,
  );
  // Add missing months starting from this month up to maxMonths, skip months that already have an entry
  let current = dayjs();
  let date = current.format(DATE_FORMAT);
  for (let i = 0; i < maxMonths; i++) {
    if (!byMonth[date]) {
      byMonth[date] = [];
    }
    current = current.add(1, "month");
    date = current.format(DATE_FORMAT);
  }

  const data = Object.entries(byMonth).map(([date, quantities]) => {
    return {
      date,
      quantity: quantities.reduce((acc, val) => acc + val, 0),
    };
  });
  data.sort((a, b) => a.date.localeCompare(b.date));
  return data;
}
